import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import {
  CurecallButton,
  CurecallTooltip,
  ECurecallButtonAppearance,
  ECurecallSize,
  RoundInfoIcon,
} from '@honestica/curecall-ui'
import { ECampaignType } from 'src/types/hr'

type TProps = Readonly<{
  campaignType: ECampaignType
  selectedCount: number
  initialMessage: string
  onNext: (message: string) => void
  onBack: () => void
}>

export const StepMessage = ({ campaignType, selectedCount, initialMessage, onNext, onBack }: TProps) => {
  const { t } = useTranslation()

  const defaultMessage =
    campaignType === ECampaignType.PLAN_BLANC
      ? t('pages.hr.campaigns.new.default-message-plan-blanc')
      : t('pages.hr.campaigns.new.default-message-alert')

  const [message, setMessage] = useState(initialMessage || defaultMessage)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (!initialMessage) {
      setMessage(defaultMessage)
    }
  }, [campaignType, defaultMessage, initialMessage])

  const typeLabel =
    campaignType === ECampaignType.PLAN_BLANC
      ? t('pages.hr.campaigns.new.plan-blanc-label')
      : t('pages.hr.campaigns.new.alert-label')

  const channelValue = campaignType === ECampaignType.PLAN_BLANC ? 'RCS, SMS' : 'SMS'

  return (
    <div className="c-flex c-flex-col c-gap-6 c-max-w-2xl c-mx-auto c-w-full">
      {/* Communication type badge */}
      <div className="c-flex c-items-center c-gap-2">
        {campaignType === ECampaignType.PLAN_BLANC ? (
          <CurecallTooltip
            content={t('pages.hr.campaigns.new.message.badge-plan-blanc-tooltip')}
            tooltipPosition="bottom"
          >
            <span className="c-inline-flex c-items-center c-gap-1.5 c-rounded-full c-bg-blue-50 c-border c-border-blue-200 c-px-3 c-py-1 c-text-xs c-font-medium c-text-blue-700 c-cursor-default">
              {t('pages.hr.campaigns.new.message.badge-plan-blanc')}
              <RoundInfoIcon className="c-w-3.5 c-h-3.5 c-shrink-0" />
            </span>
          </CurecallTooltip>
        ) : (
          <span className="c-inline-flex c-items-center c-rounded-full c-bg-yellow-50 c-border c-border-yellow-200 c-px-3 c-py-1 c-text-xs c-font-medium c-text-yellow-700">
            {t('pages.hr.campaigns.new.message.badge-alert')}
          </span>
        )}
      </div>

      {/* Message section */}
      <div className="c-flex c-flex-col c-gap-2">
        <div className="c-flex c-items-center c-justify-between">
          <label className="c-font-semibold c-text-sm">{t('pages.hr.campaigns.new.message.title')}</label>
          {!isEditing && (
            <CurecallButton
              type="button"
              appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
              size={ECurecallSize.SMALL}
              onClick={() => setIsEditing(true)}
            >
              {t('pages.hr.campaigns.new.message.edit-button')}
            </CurecallButton>
          )}
        </div>

        {isEditing ? (
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            className="c-border c-border-grey-200 c-rounded-lg c-p-3 c-text-sm c-resize-none c-w-full c-font-sans focus:c-outline-none focus:c-border-primary"
          />
        ) : (
          <div className="c-border c-border-grey-200 c-rounded-lg c-p-3 c-text-sm c-bg-grey-50 c-whitespace-pre-wrap c-min-h-24">
            {message}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="c-border c-border-grey-200 c-rounded-lg c-p-4 c-flex c-flex-col c-gap-3 c-bg-grey-50">
        <SummaryRow label={t('pages.hr.campaigns.new.message.summary-scenario')} value={typeLabel} />
        <SummaryRow
          label={t('pages.hr.campaigns.new.message.summary-recipients-label')}
          value={t('pages.hr.campaigns.new.message.summary-recipients-value', { count: selectedCount })}
        />
        <SummaryRow label={t('pages.hr.campaigns.new.message.summary-channel')} value={channelValue} />
      </div>

      {/* Navigation */}
      <div className="c-flex c-items-center c-justify-between">
        <CurecallButton
          type="button"
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.MEDIUM}
          onClick={onBack}
        >
          {t('pages.hr.campaigns.new.message.back')}
        </CurecallButton>

        <CurecallButton
          type="button"
          appearance={ECurecallButtonAppearance.PRIMARY}
          size={ECurecallSize.MEDIUM}
          isDisabled={!message.trim()}
          onClick={() => onNext(message)}
        >
          {t('pages.hr.campaigns.new.message.next')}
        </CurecallButton>
      </div>
    </div>
  )
}

const SummaryRow = ({ label, value }: Readonly<{ label: string; value: string }>) => (
  <div className="c-flex c-items-center c-gap-2">
    <span className="c-text-xs c-font-semibold c-text-grey-500 c-w-24 c-shrink-0">{label}</span>
    <span className="c-text-sm c-text-grey-800">{value}</span>
  </div>
)
